"use client";

import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

import {
  clampLayoutRect,
  isLayoutFreeForWidget,
  mergeLayout,
  mergeWidgetConfigJson,
  mergeWidgetForDisplay,
  type LayoutPatch,
} from "@/components/dashboard/dashboard-layout-utils";
import { useAppDialogs } from "@/components/dialogs/app-dialog-provider";
import { collectFormulaIdentifiers, parseStandardWidgetConfig } from "@/components/dashboard/widget-config";
import { deleteDashboardWidget, updateDashboardWidget } from "@/lib/api/endpoints/dashboards";
import { getDeviceById } from "@/lib/api/endpoints/devices";
import type { DashboardWidgetDto } from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";

export type DashboardBuilderContextValue = {
  dashboardId: string;
  baseWidgets: DashboardWidgetDto[];
  editMode: boolean;
  setEditMode: (value: boolean) => void | Promise<void>;
  selectedWidgetId: string | null;
  setSelectedWidgetId: Dispatch<SetStateAction<string | null>>;
  dirtyLayout: Record<string, LayoutPatch>;
  dirtyConfig: Record<string, JsonObject>;
  /** Full merged widget for runtime + grid (read-only projection). */
  getDisplayWidget: (base: DashboardWidgetDto) => DashboardWidgetDto;
  commitLayout: (widgetId: string, layout: LayoutPatch) => void;
  commitConfigPatch: (widgetId: string, patch: JsonObject) => void;
  replaceDirtyConfig: (widgetId: string, config: JsonObject) => void;
  clearDirty: () => void;
  isDirty: boolean;
  addModalOpen: boolean;
  setAddModalOpen: (open: boolean) => void;
  saving: boolean;
  saveError: string | null;
  saveAll: () => Promise<void>;
  deleteWidget: (widgetId: string) => Promise<void>;
};

const DashboardBuilderContext = createContext<DashboardBuilderContextValue | null>(null);

export function useDashboardBuilderOptional(): DashboardBuilderContextValue | null {
  return useContext(DashboardBuilderContext);
}

export function useDashboardBuilder(): DashboardBuilderContextValue {
  const v = useContext(DashboardBuilderContext);
  if (!v) {
    throw new Error("useDashboardBuilder must be used within DashboardBuilderProvider");
  }
  return v;
}

type DashboardBuilderProviderProps = {
  dashboardId: string;
  widgets: DashboardWidgetDto[];
  initialEditMode?: boolean;
  children: ReactNode;
};

export function DashboardBuilderProvider({
  dashboardId,
  widgets,
  initialEditMode = false,
  children,
}: DashboardBuilderProviderProps) {
  const qc = useQueryClient();
  const dialogs = useAppDialogs();
  const widgetsKey = useMemo(() => ["dashboard-widgets", dashboardId] as const, [dashboardId]);

  const [editMode, setEditModeState] = useState(initialEditMode);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [dirtyLayout, setDirtyLayout] = useState<Record<string, LayoutPatch>>({});
  const [dirtyConfig, setDirtyConfig] = useState<Record<string, JsonObject>>({});
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isDirty = useMemo(
    () => Object.keys(dirtyLayout).length > 0 || Object.keys(dirtyConfig).length > 0,
    [dirtyLayout, dirtyConfig],
  );

  const clearDirty = useCallback(() => {
    setDirtyLayout({});
    setDirtyConfig({});
    setSaveError(null);
  }, []);

  const setEditMode = useCallback(
    async (value: boolean) => {
      if (!value && isDirty) {
        const confirmed = await dialogs.confirm({
          title: "Discard unsaved changes?",
          description: "Layout and configuration updates that have not been saved will be lost.",
          confirmLabel: "Discard changes",
          tone: "danger",
        });
        if (!confirmed) {
          return;
        }
        clearDirty();
      }
      setEditModeState(value);
      if (!value) {
        setSelectedWidgetId(null);
        setAddModalOpen(false);
      }
    },
    [isDirty, clearDirty, dialogs],
  );

  const getDisplayWidget = useCallback(
    (base: DashboardWidgetDto): DashboardWidgetDto => {
      return mergeWidgetForDisplay(base, dirtyLayout[base.id] ?? null, dirtyConfig[base.id] ?? null);
    },
    [dirtyLayout, dirtyConfig],
  );

  const commitLayout = useCallback((widgetId: string, patch: LayoutPatch) => {
    setDirtyLayout((prev) => {
      const base = widgets.find((w) => w.id === widgetId);
      if (!base) {
        return prev;
      }
      const current = mergeLayout(base, prev[widgetId] ?? null);
      const proposed = clampLayoutRect({
        position_x: patch.position_x ?? current.position_x,
        position_y: patch.position_y ?? current.position_y,
        width: patch.width ?? current.width,
        height: patch.height ?? current.height,
      });
      const nextDirty = { ...prev, [widgetId]: proposed };
      if (!isLayoutFreeForWidget(proposed, widgetId, widgets, nextDirty)) {
        return prev;
      }
      return nextDirty;
    });
  }, [widgets]);

  const commitConfigPatch = useCallback((widgetId: string, patch: JsonObject) => {
    setDirtyConfig((prev) => ({
      ...prev,
      [widgetId]: { ...(prev[widgetId] ?? {}), ...patch },
    }));
  }, []);

  const replaceDirtyConfig = useCallback((widgetId: string, config: JsonObject) => {
    setDirtyConfig((prev) => ({ ...prev, [widgetId]: { ...config } }));
  }, []);

  const saveAll = useCallback(async () => {
    setSaveError(null);
    const ids = new Set([...Object.keys(dirtyLayout), ...Object.keys(dirtyConfig)]);
    if (ids.size === 0) {
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        [...ids].map(async (id) => {
          const w = widgets.find((x) => x.id === id);
          if (!w) {
            return;
          }
          const layout = mergeLayout(w, dirtyLayout[id] ?? null);
          const fullConfig = mergeWidgetConfigJson(w.config, dirtyConfig[id] ?? null);
          const parsed = parseStandardWidgetConfig(fullConfig);
          if (!parsed.ok) {
            throw new Error(`Widget ${id.slice(0, 8)}…: ${parsed.error}`);
          }
          const device = await getDeviceById(parsed.data.device_id);
          const keys = new Set((device.channels ?? []).map((c) => c.key));
          if (parsed.data.channel) {
            if (!keys.has(parsed.data.channel)) {
              throw new Error(
                `Widget ${id.slice(0, 8)}…: channel "${parsed.data.channel}" is not defined for the selected device.`,
              );
            }
          } else if (parsed.data.formula) {
            for (const ident of collectFormulaIdentifiers(parsed.data.formula)) {
              if (!keys.has(ident)) {
                throw new Error(
                  `Widget ${id.slice(0, 8)}…: formula references unknown channel "${ident}" for this device.`,
                );
              }
            }
          }
          await updateDashboardWidget(id, {
            position_x: layout.position_x,
            position_y: layout.position_y,
            width: layout.width,
            height: layout.height,
            config: fullConfig,
          });
        }),
      );
      clearDirty();
      await qc.invalidateQueries({ queryKey: widgetsKey });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }, [widgets, dirtyLayout, dirtyConfig, qc, widgetsKey, clearDirty]);

  const deleteWidget = useCallback(
    async (widgetId: string) => {
      const confirmed = await dialogs.confirm({
        title: "Delete widget?",
        description: "This removes the widget from the dashboard layout immediately.",
        confirmLabel: "Delete widget",
        tone: "danger",
      });
      if (!confirmed) {
        return;
      }
      setSaveError(null);
      const prev = qc.getQueryData<DashboardWidgetDto[]>(widgetsKey);
      qc.setQueryData<DashboardWidgetDto[]>(widgetsKey, (old) => (old ?? []).filter((x) => x.id !== widgetId));
      try {
        await deleteDashboardWidget(widgetId);
        setDirtyLayout((d) => {
          const n = { ...d };
          delete n[widgetId];
          return n;
        });
        setDirtyConfig((d) => {
          const n = { ...d };
          delete n[widgetId];
          return n;
        });
        setSelectedWidgetId((s) => (s === widgetId ? null : s));
        await qc.invalidateQueries({ queryKey: widgetsKey });
      } catch (e) {
        if (prev) {
          qc.setQueryData(widgetsKey, prev);
        }
        setSaveError(e instanceof Error ? e.message : "Delete failed.");
      }
    },
    [dialogs, qc, widgetsKey],
  );

  const value = useMemo<DashboardBuilderContextValue>(
    () => ({
      dashboardId,
      baseWidgets: widgets,
      editMode,
      setEditMode,
      selectedWidgetId,
      setSelectedWidgetId,
      dirtyLayout,
      dirtyConfig,
      getDisplayWidget,
      commitLayout,
      commitConfigPatch,
      replaceDirtyConfig,
      clearDirty,
      isDirty,
      addModalOpen,
      setAddModalOpen,
      saving,
      saveError,
      saveAll,
      deleteWidget,
    }),
    [
      dashboardId,
      widgets,
      editMode,
      setEditMode,
      selectedWidgetId,
      dirtyLayout,
      dirtyConfig,
      getDisplayWidget,
      commitLayout,
      commitConfigPatch,
      replaceDirtyConfig,
      clearDirty,
      isDirty,
      addModalOpen,
      saving,
      saveError,
      saveAll,
      deleteWidget,
    ],
  );

  return <DashboardBuilderContext.Provider value={value}>{children}</DashboardBuilderContext.Provider>;
}
